import React, { useEffect, useRef, useState } from 'react';
import {
    getFramework,
    getLayerId,
    getOperateType,
    MoveListenerLevel,
    useIdentity,
    useMoveEventListener,
} from '@/framework';
import { isHotKeyPress, ITouchKitEvent } from '@/utils/event-kit';
import { BaseLayerViewType, ILayerProps, IPoint } from '@/version-types';
import { useMoveOperate } from '../hooks/use-move-operate';

export const MoveOperate: React.FC<{ layer: ILayerProps }> = ({ layer }) => {
    const identity = useIdentity();
    const moveOperate = useMoveOperate();
    const framework = getFramework(identity);
    const downPoint = useRef<IPoint>({ x: 0, y: 0 });
    const isMove = useRef(false);
    const clickTimer = useRef<NodeJS.Timeout | null>(null);

    function checkCanMove(e: ITouchKitEvent) {
        if (framework.operate.isDisableMove()) return;
        const target = e.target;
        if (!target) return;
        const operateType = getOperateType(target);
        if (operateType !== BaseLayerViewType.MOVE) {
            // 检查是否点击到图层上
            const layerId = getLayerId(identity, target);
            if (layerId && !framework.getLayer(layerId).lock) return layer.id;
            return;
        }
        // 点击到非移动操作按钮
        // if (operateType && operateType !== BaseLayerViewType.MOVE) return;
        return layer.id;
    }

    const activeTimerRef = useRef<number>(new Date().getTime());
    useEffect(() => {
        activeTimerRef.current = new Date().getTime();
    }, [layer.id]);

    function processClick() {
        if (isHotKeyPress()) return false;
        // 选中组内元素，需要延迟处理点击，防止从父级继承过来的点击
        // ATTENTION: 将时间判断减少到 100 ms，修复非组内的文本图层快速双击无法直接进入编辑态的问题
        if (new Date().getTime() - activeTimerRef.current < 100) return false;
        if (isMove.current) return false;
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
        return true;
    }

    useMoveEventListener(
        (e) => {
            const id = checkCanMove(e);
            if (!id) return;
            if (e.isStart) {
                moveOperate.beforeMove(id);
                framework.operate.delHoverId();
            }
        },
        MoveListenerLevel.PREPARE_LEVEL,
        'move-pre',
    );

    useMoveEventListener(
        (e) => {
            const id = checkCanMove(e);
            if (!id) return;
            moveOperate.move(id, e);
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'move-default',
    );

    const [isDisableMove, setIsDisableMove] = useState(framework.operate.isDisableMove());

    useEffect(() => {
        const handler = framework.events.onEditorOperateState((state) => {
            setIsDisableMove(state.disableMove);
        });

        return () => {
            framework.events.off(handler);
        };
    }, []);

    if (isDisableMove) return null;

    return (
        <div
            data-operate-type={BaseLayerViewType.MOVE}
            className="active-operate__move"
            onMouseDown={(e) => {
                downPoint.current = { x: e.pageX || 0, y: e.pageY || 0 };
                isMove.current = false;
            }}
            onMouseUp={(e) => {
                const currentPoint = { x: e.pageX || 0, y: e.pageY || 0 };
                if (currentPoint.x !== downPoint.current.x || currentPoint.y !== downPoint.current.y) {
                    isMove.current = true;
                }
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (!processClick()) return;
                clickTimer.current = setTimeout(() => {
                    framework.events.emitLayerClick(layer.id, {
                        x: e.pageX || 0,
                        y: e.pageY || 0,
                    });
                }, 300);
            }}
            onDoubleClick={(e) => {
                e.stopPropagation();
                if (!processClick()) return;
                framework.events.emitLayerDoubleClick(layer.id, {
                    x: e.pageX || 0,
                    y: e.pageY || 0,
                });
            }}
        ></div>
    );
};
